/**
 * Parcours de caisse apres la connexion par NIP : un useReducer(fluxCaisse)
 * du contrat (vente/contrat.js), le meme pour les PR 1.5a et 1.5b.
 *   OUVERTURE -> <OuvertureCaisse> (aucun quart ouvert)
 *   VENTE     -> <EcranVente onEncaisser panierInitial> (recu en prop : PR 1.5a)
 *   PAIEMENT  -> <EcranPaiement>
 *   RECU      -> <EcranRecu>
 *
 * Le panier recu de l'ecran de vente est verifie (verifierPanier) avant le
 * paiement : un panier invalide est refuse avec un message clair, l'ecran de
 * vente reste en place. Les transitions en double (double clic sur
 * « Encaisser » ou « Terminer la vente ») sont ignorees par le reducteur.
 *
 * Branche dans app/App.jsx (session ouverte, vente permise) avec l'ecran de
 * vente de la PR 1.5a. `ecranVenteProps` : ce que l'ecran de vente demande
 * au poste (terminal, db, api, catalogue, synchro...), passe tel quel ; le
 * flux y ajoute session, panierInitial, onEncaisser et onPanierNonVide.
 * `onPanierNonVide` et `onEtatChange` servent a venteEnCours (UpdatePrompt).
 * `onLock` : « Verrouiller » de l'ouverture de caisse.
 */
import { useCallback, useEffect, useReducer, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import EcranPaiement from './EcranPaiement';
import EcranRecu from './EcranRecu';
import OuvertureCaisse from './OuvertureCaisse';
import { palette } from '../app/theme';
import { getOpenShift } from '../store/shifts';
import {
    ACTIONS_CAISSE, ETAPES_CAISSE, ETAT_CAISSE_INITIAL, fluxCaisse, verifierPanier,
} from '../vente/contrat';

export default function FluxCaisse({
    db, session, EcranVente, ecranVenteProps = {}, onPanierNonVide, onLock, imprimer, now, onEtatChange,
}) {
    const [state, dispatch] = useReducer(fluxCaisse, ETAT_CAISSE_INITIAL);
    const [quartLu, setQuartLu] = useState(false);
    const [refus, setRefus] = useState(null);

    useEffect(() => {
        let cancelled = false;
        getOpenShift(db)
            .then((shift) => {
                if (cancelled) return;
                dispatch(shift ? { type: ACTIONS_CAISSE.QUART_OUVERT, shift_id: shift.shift_id } : { type: ACTIONS_CAISSE.QUART_ABSENT });
                setQuartLu(true);
            })
            .catch(() => {
                if (cancelled) return;
                dispatch({ type: ACTIONS_CAISSE.QUART_ABSENT });
                setQuartLu(true);
            });
        return () => { cancelled = true; };
    }, [db]);

    useEffect(() => {
        if (onEtatChange) onEtatChange(state);
    }, [state, onEtatChange]);

    const encaisser = useCallback((panier) => {
        const erreurs = verifierPanier(panier);
        if (erreurs.length > 0) {
            setRefus(erreurs);
            return;
        }
        setRefus(null);
        dispatch({ type: ACTIONS_CAISSE.ENCAISSER, panier });
    }, []);

    // Chaque etape monte son propre ecran : l'ecran de vente repart du panier
    // rendu (RETOUR_VENTE) ou d'un panier vide (NOUVELLE_VENTE).
    const nouvelleVente = useCallback(() => dispatch({ type: ACTIONS_CAISSE.NOUVELLE_VENTE }), []);

    if (!quartLu) return <Box data-testid="flux-chargement" aria-busy="true" sx={{ flex: 1 }} />;

    switch (state.etape) {
    case ETAPES_CAISSE.OUVERTURE:
        return (
            <OuvertureCaisse
                db={db}
                session={session}
                now={now}
                onVerrouiller={onLock}
                poste={{ sync: ecranVenteProps.sync, onSync: ecranVenteProps.onSync, canSync: ecranVenteProps.canSync }}
                onOuverte={(shift) => dispatch({ type: ACTIONS_CAISSE.QUART_OUVERT, shift_id: shift.shift_id })}
            />
        );
    case ETAPES_CAISSE.VENTE:
        return (
            <Box data-testid="flux-vente" sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                {refus && (
                    <Typography role="alert" data-testid="panier-refuse" sx={{ m: 2, mb: 0, p: 2, borderRadius: 2, fontSize: 18, fontWeight: 600, bgcolor: palette.errorTint, color: palette.errorOnTint }}>
                        Ce panier ne peut pas être encaissé : il est incomplet ({refus.slice(0, 5).join(', ')}). Corrigez le panier ; si le problème continue, appelez le responsable.
                    </Typography>
                )}
                <EcranVente
                    {...ecranVenteProps}
                    session={session}
                    shiftId={state.shift_id}
                    panierInitial={state.panier}
                    onEncaisser={encaisser}
                    onPanierNonVide={onPanierNonVide}
                />
            </Box>
        );
    case ETAPES_CAISSE.PAIEMENT:
        return (
            <EcranPaiement
                db={db}
                panier={state.panier}
                shiftId={state.shift_id}
                now={now}
                onRetourVente={() => dispatch({ type: ACTIONS_CAISSE.RETOUR_VENTE })}
                onVenteTerminee={(vente) => dispatch({ type: ACTIONS_CAISSE.VENTE_TERMINEE, vente })}
            />
        );
    case ETAPES_CAISSE.RECU:
        return (
            <EcranRecu
                key={state.vente.sale_uuid}
                db={db}
                vente={state.vente}
                imprimer={imprimer}
                onNouvelleVente={nouvelleVente}
            />
        );
    default:
        return null;
    }
}
